import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BillingProvider } from "@/context/BillingContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import ProgramIdentity from "@/pages/ProgramIdentity";
import StaffManual from "@/pages/StaffManual";
import PlayerHandbook from "@/pages/PlayerHandbook";
import PracticePlanner from "@/pages/PracticePlanner";
import AICoach from "@/pages/AICoach";
import Playbook from "@/pages/Playbook";
import CultureBuilder from "@/pages/CultureBuilder";
import FilmHub from "@/pages/FilmHub";
import DrillLibrary from "@/pages/DrillLibrary";
import PracticePrint from "@/pages/PracticePrint";
import Pricing from "@/pages/Pricing";
import BillingSettings from "@/pages/BillingSettings";
import ScoutReport from "@/pages/ScoutReport";
import Roster from "@/pages/Roster";
import RosterPrint from "@/pages/RosterPrint";
import DocSectionsPrint from "@/pages/DocSectionsPrint";
import Messaging from "@/pages/Messaging";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import ModulePlaceholder from "@/pages/ModulePlaceholder";

function HomeGate() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="font-display text-3xl text-amber tracking-[0.2em]" data-testid="home-loading">
          LOADING…
        </div>
      </div>
    );
  }
  if (!user) return <Landing />;
  return (
    <AppLayout>
      <Dashboard />
    </AppLayout>
  );
}

function App() {
  useEffect(() => {
    // Hard-redirect legacy preview URL to the canonical custom domain.
    // Bypass with ?preview=1 for internal previews.
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("preview") === "1") return;
      const host = window.location.hostname;
      const legacyHosts = ["gridiron-os.preview.emergentagent.com", "www.gridiron-os.com"];
      if (legacyHosts.includes(host)) {
        window.location.replace(
          "https://gridiron-os.com" + window.location.pathname + window.location.search + window.location.hash
        );
      }
    }
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BillingProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomeGate />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/*" element={
              <ProtectedRoute>
                <AppLayout>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/program-identity" element={<ProgramIdentity />} />
                    <Route path="/staff-manual" element={<StaffManual />} />
                    <Route path="/player-handbook" element={<PlayerHandbook />} />
                    <Route path="/practice-planner" element={<PracticePlanner />} />
                    <Route path="/practice-planner/:id/print" element={<PracticePrint />} />
                    <Route path="/ai-coach" element={<AICoach />} />
                    <Route path="/culture" element={<CultureBuilder />} />
                    <Route path="/drill-library" element={<DrillLibrary />} />
                    <Route path="/playbook" element={<Playbook />} />
                    <Route path="/film-hub" element={<FilmHub />} />
                    <Route path="/roster" element={<Roster />} />
                    <Route path="/roster/print" element={<RosterPrint />} />
                    <Route path="/documents/print/:kind" element={<DocSectionsPrint />} />
                    <Route path="/player-development" element={<ModulePlaceholder />} />
                    <Route path="/calendar" element={<ModulePlaceholder />} />
                    <Route path="/documents" element={<ModulePlaceholder />} />
                    <Route path="/messaging" element={<Messaging />} />
                    <Route path="/knowledge-base" element={<ModulePlaceholder />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/settings/billing" element={<BillingSettings />} />
                    <Route path="/scout-report/:id" element={<ScoutReport />} />
                    <Route path="*" element={<Dashboard />} />
                  </Routes>
                </AppLayout>
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
        </BillingProvider>
      </AuthProvider>
      <Toaster theme="dark" position="top-right" toastOptions={{
        style: {
          background: "#14171F",
          border: "1px solid #272E3F",
          color: "#F8FAFC",
          borderRadius: "2px",
        }
      }} />
    </div>
  );
}

export default App;
